import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTimeSeriesData } from "../../api/timeseriesApi";
import { selectManyTimeSeriesData } from "../../store/slices/timeseries.slice";
import { getDateFromLookback } from "../../utils/datetime";
import { Row, Col, Divider, Modal, Typography } from 'antd';
import Plot from 'react-plotly.js';
import TextViewer from '../../components/TextViewer';
import VolatilitySummaryCard from './VolatilitySummaryCard';
import YieldSummaryCard from './YieldSummaryCard';
import ChartComponent from './ChartComponent';
import NavTrail from '../../layouts/NavTrail';
import EnhancedVolatilityChart from "./EnhancedVolatilityChart";

const { Title } = Typography;

const VolatilityIndices = [
  '^vix9d',
  '^vix',
  '^vix6m',
];

const YieldIndices = [
  '^tnx',
  '^fvx',
  '^irx',
];

const FactorView = () => {
  const dispatch = useDispatch();
  const timeSeriesData = useSelector(state => selectManyTimeSeriesData(state, [...VolatilityIndices, ...YieldIndices]));
  const [isModalVisible, setIsModalVisible] = useState(false);

  useEffect(() => {
    const lookback = 60;
    const startDate = getDateFromLookback(lookback, 'day');
    const symbols = [...VolatilityIndices, ...YieldIndices];
    dispatch(fetchTimeSeriesData({ symbols, startDate }));
  }, [dispatch]);

  // Helper function to format data for Plotly
  const formatPlotData = (indices) => {
    return indices.map(index => {
      const symbolData = timeSeriesData.find(item => item.symbol === index);
      if (!symbolData) return null;

      return {
        x: symbolData.data.map(point => point.timestamp),
        y: symbolData.data.map(point => point.close),
        high: symbolData.data.map(point => point.high),
        low: symbolData.data.map(point => point.low),
        type: 'scatter',
        mode: 'lines+markers',
        name: index
      };
    }).filter(Boolean);
  };

  // Extract summary data for Volatility and Yields
  const getLatestData = (symbol) => {
    const data = timeSeriesData.find(item => item.symbol === symbol)?.data;
    return data ? data[data.length - 1] : null;
  };

  const latestVix = getLatestData('^vix');
  const latestTnx = getLatestData('^tnx');

  // Determine contango/backwardation for volatility
  const contangoStatus = latestVix && getLatestData('^vix6m')?.close > latestVix.close ? "Contango" : "Backwardation";
  console.log(latestTnx, getLatestData('^irx'));
  // Determine yield curve inversion status
  const yieldCurveStatus = getLatestData('^irx')?.close > latestTnx?.close ? "Inverted" : "Normal";

  // Volatility Term Structure Data
  const volatilityData = formatPlotData(VolatilityIndices);
  // Yield Curve Data
  const yieldData = formatPlotData(YieldIndices);

  const trailConfig = [
    {label: "BETA", path: '/beta/launcher'},
    {label: "Factor View"}
  ]

  return (
    <div>
      <NavTrail items={trailConfig}/>
      <Row gutter={[32, 32]} className={'p-6'}>
        {/* Summary Cards */}
        <Col xs={24} xl={24} xxl={12}>
          <VolatilitySummaryCard timeSeriesData={volatilityData} />
          <Divider/>
          <ChartComponent
            data={volatilityData}
            title="Volatility Indices Term Structure"
            layout={{
              xaxis: { title: 'Date' },
              yaxis: { title: 'Index Value' },
              showlegend: true,
              margin: { t: 10, b: 30, l: 40, r: 10 },
            }}
          />
          <EnhancedVolatilityChart data={volatilityData} />
        </Col>
        <Col xs={24} xl={24} xxl={12}>
          <YieldSummaryCard timeSeriesData={yieldData} />
          <Divider/>
          <ChartComponent
            data={yieldData}
            title="Treasury Yield Curve Analysis"
            layout={{
              xaxis: { title: 'Date' },
              yaxis: { title: 'Yield (%)' },
              showlegend: true,
              margin: { t: 10, b: 30, l: 40, r: 10 },
            }}
          />
        </Col>
      </Row>


    </div>
  );
};

export default FactorView;
